import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  searchPublicArticles,
  selectSearchedPublicArticles,
  selectFeaturedOnly,
  selectKeywords,
  selectPage,
  selectLastSearchKey,
  setFeaturedOnly,
  setKeywords,
  setPage,
  setLastSearchKey,
} from 'app/store/marketing/publicArticlesSlice';
import { selectIsLoading } from 'app/store/utilSlice';
import ArticlePreview from 'app/shared-components/marketing/ArticlePreview';
import EastIcon from '@mui/icons-material/East';
import Input from 'app/shared-components/marketing/Input';
import Button from 'app/shared-components/marketing/Button';
import Breadcrumbs from 'app/shared-components/marketing/Breadcrumbs';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import CircularProgress from '@mui/material/CircularProgress';

const ARTICLES_PER_PAGE = 9;

export default function CommunityArticles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const featuredOnly = useSelector(selectFeaturedOnly());
  const keywords = useSelector(selectKeywords());
  const page = useSelector(selectPage());
  const lastSearchKey = useSelector(selectLastSearchKey());
  const isLoading = useSelector(selectIsLoading);

  const urlKeywords = searchParams.get('s') || '';

  // Sync URL keywords with slice state
  useEffect(() => {
    if (urlKeywords !== keywords) {
      dispatch(setKeywords(urlKeywords));
    }
  }, [urlKeywords, keywords, dispatch]);

  // Execute search only when criteria change
  useEffect(() => {
    const searchKey = `${keywords}-${featuredOnly}-${page}`;
    if (searchKey !== lastSearchKey) {
      dispatch(searchPublicArticles({
        keywords: keywords,
        featured: featuredOnly || undefined,
        limit: ARTICLES_PER_PAGE,
        offset: ARTICLES_PER_PAGE * (page - 1),
      }));
      dispatch(setLastSearchKey(searchKey));
    }
  }, [keywords, featuredOnly, page, lastSearchKey, dispatch]);

  const searchedArticles = useSelector(selectSearchedPublicArticles());

  const onSearchSubmit = (e) => {
    e.preventDefault();
    
    const data = new FormData(e.target);
    const newKeywords = data.get('s');
    setSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      s: newKeywords
    });
  }

  const toggleFeatured = (e) => {
    dispatch(setFeaturedOnly(e.target.checked));
  }

  const showMore = () => {
    dispatch(setPage(page + 1));
  }

  return (
    <div>
      {/* Breadcrumbs */}
      <section>
        <div className="container py-28">
          <Breadcrumbs
            trail={[
              { label: 'Home', to: '/' },
              { label: 'Blog', to: '/blog' },
              { label: 'Community Articles', to: '/community-articles' }
            ]}
          />
        </div>
      </section>

      {/* Articles */}
      <section>
        <div className="container pb-80">
          <h1 className="text-48 text-hs-grey-black leading-none font-medium">
            Community Articles
          </h1>

          <div className="flex flex-col gap-20 mt-40">
            <form onSubmit={onSearchSubmit}>
              <Input
                placeholder="Search Community Articles"
                name="s"
                defaultValue={urlKeywords}
              />
            </form>
            
            <FormControlLabel
              control={
                <Switch
                  checked={featuredOnly}
                  onChange={toggleFeatured}
                  color="primary"
                />
              }
              label="Featured Only"
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-32 mt-40">
              <CircularProgress />
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-20 mt-40">
              {searchedArticles?.map((article) => (
                <ArticlePreview key={article.id} article={article} />
              ))}
            </div>
          )}

          <div className="mt-40">
            {searchedArticles?.length === ARTICLES_PER_PAGE * page && (
              <Button
                className="text-hs-blue outline-hs-blue"
                onClick={showMore}
              >
                Show More <EastIcon className="ml-2" />
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}